import {computed, inject, Injectable, OnDestroy, signal} from '@angular/core';
import {firstValueFrom, Subject, takeUntil} from 'rxjs';

import {AuthService} from '@core/authentication/auth.service';
import {ConversationApiService} from '@shared/services/api/conversation/conversation-api.service';
import {IUserBusiness} from '@shared/models/user/user.model';
import {DEFAULT_AVATAR_URL} from '@shared/constants/ui.constants';
import {getUserDisplayName} from '@shared/utils/strings.utils';

const GRAPH_PICTURE = (fbUid: string) =>
  `https://graph.facebook.com/${fbUid}/picture?height=100&width=100`;

/**
 * Vai trò loại khỏi danh sách "nhân viên thuần" (không bao gồm giám đốc/admin).
 * roleId (string, hệ quyền mới) là nguồn chuẩn; role số đã deprecated ở BE (BusinessUser::getRole) và không đổi khi sửa
 * quyền nhân viên → chỉ dùng cho bản ghi chưa có roleId.
 */
const EXCLUDED_ROLE_IDS_NUMERIC = [1, 2, 100]; // SuperAdmin, Admin, Business_Manager
const EXCLUDED_ROLE_IDS_STRING = [
  'SYSTEM_ROLE_SUPER_ADMIN',
  'SYSTEM_ROLE_ADMIN',
  'SYSTEM_ROLE_MANAGER',
];

/**
 * Thông tin nhân viên đã resolve trong phạm vi doanh nghiệp hiện tại.
 * `found` phải được kiểm tra trước khi dùng dữ liệu để tránh hiển thị nhân viên
 * thuộc doanh nghiệp cũ sau khi page được chuyển doanh nghiệp.
 */
export interface ICurrentBusinessUserIdentity {
  id: string;
  name: string;
  avatarUrl: string;
  found: boolean;
}

/**
 * ── BusinessUserService ──────────────────────────────────────────────────────
 * Service dùng chung toàn app quản lý danh sách nhân viên theo doanh nghiệp.
 *
 * Trách nhiệm:
 * 1. Tự động load nhân viên khi app khởi tạo (F5) hoặc khi chuyển doanh nghiệp.
 * 2. Cache kết quả trong phiên giao diện — component nào cũng có thể truy cập.
 * 3. Cung cấp helper filter (bỏ giám đốc/admin, tìm theo id, resolve avatar).
 * 4. Cung cấp mutation methods (add/update/remove) để các trang Settings
 * gọi sau khi BE xử lý xong → đồng bộ store mà không cần gọi lại API.
 *
 * Cách dùng:
 * - inject BusinessUserService vào bất kỳ component/service nào.
 * - getAll() / getStaffOnly() để lấy danh sách.
 * - Sau khi sửa/xoá NV ở trang Settings → gọi updateUser/removeUser để store tự cập nhật.
 */
@Injectable({providedIn: 'root'})
export class BusinessUserService implements OnDestroy {
  private readonly auth = inject(AuthService);
  private readonly api = inject(ConversationApiService);

  // ── Internal state ────────────────────────────────────────────────────────
  /** Store nhân viên theo businessId */
  private readonly _store = signal<ReadonlyMap<string, readonly IUserBusiness[]>>(new Map());

  /** Danh sách nhân viên tổng hợp của tất cả các doanh nghiệp đã chọn (deduplicate theo ID) */
  private readonly _allUsers = signal<readonly IUserBusiness[]>([]);

  /** Đánh dấu businessId đã load xong (tránh gọi lại API) */
  private readonly _loaded = new Set<string>();

  /** Đánh dấu businessId đang load (guard race condition — tránh gọi API 3 lần) */
  private readonly _loadingBusinessIds = new Set<string>();

  /** Đánh dấu đang load dữ liệu */
  private readonly _loading = signal<boolean>(false);

  private readonly _destroy$ = new Subject<void>();

  // ── Public read-only signals ───────────────────────────────────────────────
  /**
   * Danh sách nhân viên (signal).
   * Ưu tiên danh sách tổng hợp của tất cả doanh nghiệp đã chọn (_allUsers).
   * Nếu chưa có _allUsers, fallback về danh sách của doanh nghiệp hiện tại hoặc toàn bộ store.
   */
  readonly users = computed(() => {
    const all = this._allUsers();
    if (all.length > 0) return all;

    const bid = this.auth.businessCurrent?.id;
    if (bid) {
      const currentList = this._store().get(String(bid));
      if (currentList && currentList.length > 0) return currentList;
    }

    // Fallback nếu không có activeBusiness: gộp toàn bộ store
    const fallbackList: IUserBusiness[] = [];
    const seen = new Set<string>();
    for (const list of this._store().values()) {
      for (const u of list) {
        const key = String(u.id ?? u.userId ?? '');
        if (key && !seen.has(key)) {
          seen.add(key);
          fallbackList.push(u);
        }
      }
    }
    return fallbackList;
  });

  /** Đang load dữ liệu hay không */
  readonly loading = this._loading.asReadonly();

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  constructor() {
    // Lắng nghe thay đổi doanh nghiệp → tự động load nhân viên
    this.auth.activeBusiness$.pipe(takeUntil(this._destroy$)).subscribe(business => {
      if (business?.id) {
        this.loadUsersForBusiness(String(business.id));
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  // ── Load ──────────────────────────────────────────────────────────────────

  /**
   * Tải toàn bộ nhân viên theo danh sách các doanh nghiệp đang được chọn (aBids).
   * Tương đương v1: ConversationService.getAllUserBusiness()
   * BE: POST /auth/loadusers với { businessIds, limit: 200 }
   */
  async loadAllUsersForSelectedBusinesses(): Promise<void> {
    const rawBids: any = this.auth.getBusinessAllSelectManagement();
    let businessIds: (string | number)[] = [];

    if (Array.isArray(rawBids)) {
      businessIds = rawBids.filter(id => id != null && String(id).trim() !== '');
    } else if (typeof rawBids === 'string' && rawBids.trim()) {
      businessIds = rawBids.split(',').map((s: string) => s.trim()).filter(Boolean);
    }

    if (!businessIds.length && this.auth.businessCurrent?.id) {
      businessIds = [this.auth.businessCurrent.id];
    }

    if (!businessIds.length) return;

    this._loading.set(true);
    try {
      // Gọi API POST /auth/loadusers chuẩn như bản cũ v1
      const res = await firstValueFrom(
        this.api.loadUsersByBusinessId({
          businessIds: businessIds.map(id => Number(id) || id),
          limit: 200,
        }),
      );

      const items = this.extractItems(res);
      if (items && items.length > 0) {
        const prev = this._store();
        const next = new Map(prev);

        items.forEach(user => {
          const bId = String(user.businessId ?? '');
          if (bId) {
            const currentList = next.get(bId) || [];
            if (!currentList.some(u => String(u.id ?? u.userId) === String(user.id ?? user.userId))) {
              next.set(bId, [...currentList, user]);
            }
          }
        });

        businessIds.forEach(id => this._loaded.add(String(id)));
        this._store.set(next);

        // Lưu vào _allUsers danh sách deduplicate theo ID (tương đương v1 Map item.id)
        const userMap = new Map<string, IUserBusiness>();
        items.forEach(u => {
          const uid = String(u.id ?? u.userId ?? '');
          if (uid) userMap.set(uid, u);
        });
        for (const list of next.values()) {
          for (const u of list) {
            const uid = String(u.id ?? u.userId ?? '');
            if (uid && !userMap.has(uid)) {
              userMap.set(uid, u);
            }
          }
        }
        this._allUsers.set(Array.from(userMap.values()));
      }
    } catch (err) {
      console.error('[BusinessUserService] Failed to load users for selected businesses:', err);
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Gọi API lấy toàn bộ nhân viên theo businessId.
   * Sử dụng API /auth/loadusers chuẩn của bản v1 thay vì /business/user/list.
   * Nếu đã load rồi → skip (dùng forceRefresh nếu muốn bắt buộc gọi lại).
   */
  async loadUsersForBusiness(businessId: string): Promise<void> {
    if (!businessId || this._loaded.has(businessId)) return;
    // Đang load cho businessId này rồi → tránh gọi lại (fix race condition 3 calls)
    if (this._loadingBusinessIds.has(businessId)) {
      await new Promise<void>(resolve => {
        const waitForCurrentLoad = () => {
          if (!this._loadingBusinessIds.has(businessId)) {
            resolve();
            return;
          }
          setTimeout(waitForCurrentLoad, 20);
        };
        waitForCurrentLoad();
      });
      return;
    }

    this._loadingBusinessIds.add(businessId);
    this._loading.set(true);
    try {
      // Gọi API POST /auth/loadusers chuẩn logic v1 (loadUsersByBusinessId)
      const res = await firstValueFrom(
        this.api.loadUsersByBusinessId({businessId: Number(businessId) || businessId, limit: 500}),
      );

      const items = this.extractItems(res);
      const prev = this._store();
      const next = new Map(prev);
      next.set(businessId, items);

      this._store.set(next);
      this._loaded.add(businessId);

      // Cập nhật lại danh sách tổng hợp
      this.syncAllUsers();
    } catch (err) {
      console.error(`[BusinessUserService] Failed to load users for business ${businessId}:`, err);
    } finally {
      this._loadingBusinessIds.delete(businessId);
      this._loading.set(false);
    }
  }

  /**
   * Xoá cache cho 1 DN và gọi lại API.
   * Dùng sau khi thêm/sửa/xoá nhân viên nếu muốn đảm bảo dữ liệu mới nhất.
   */
  async forceRefresh(businessId?: string): Promise<void> {
    const bid = businessId ?? String(this.auth.businessCurrent?.id ?? '');
    if (!bid) return;

    this._loaded.delete(bid);
    const prev = this._store();
    const next = new Map(prev);
    next.delete(bid);
    this._store.set(next);

    await this.loadUsersForBusiness(bid);
  }

  // ── Queries ───────────────────────────────────────────────────────────────

  /**
   * Lấy toàn bộ nhân viên của DN (từ cache).
   * Nếu cache chưa có → trigger load bất đồng bộ và trả về [].
   * Subscribe signal `users` để nhận dữ liệu khi load xong.
   */
  getAll(businessId?: string): readonly IUserBusiness[] {
    const bid = businessId ?? String(this.auth.businessCurrent?.id ?? '');
    if (bid) {
      const cached = this._store().get(bid);
      if (cached && cached.length > 0) return cached;
    }

    // Nếu không truyền businessId hoặc chưa có theo bid cụ thể,
    // fallback trả về danh sách tổng hợp nếu có
    if (!businessId && this._allUsers().length > 0) {
      return this._allUsers();
    }

    // Chưa có trong cache → trigger load bất đồng bộ (nếu chưa ai load)
    if (bid && !this._loaded.has(bid) && !this._loadingBusinessIds.has(bid)) {
      this.loadUsersForBusiness(bid);
    }
    return [];
  }

  /**
   * Lấy danh sách nhân viên, loại bỏ giám đốc / admin / super admin.
   * Phù hợp cho các dialog chọn nhân viên (assignee, reminder, split...).
   */
  getStaffOnly(businessId?: string): IUserBusiness[] {
    if (businessId) {
      return [...this.getAll(businessId)].filter(u => this.isStaff(u));
    }
    return [...this.users()].filter(u => this.isStaff(u));
  }

  /**
   * Tìm 1 nhân viên theo userId (sync, từ cache).
   * Trả về undefined nếu chưa load hoặc không tìm thấy.
   */
  getById(userId: string | number, businessId?: string): IUserBusiness | undefined {
    const strId = String(userId);
    if (!strId) return undefined;

    // Nếu có businessId cụ thể, tìm trong business đó trước
    if (businessId) {
      return this.getAll(businessId).find(u => String(u.id) === strId || String(u.userId) === strId);
    }

    // Tìm trong danh sách users tổng hợp
    return this.users().find(u => String(u.id) === strId || String(u.userId) === strId);
  }

  /**
   * Resolve tên và avatar của nhân viên chỉ trong doanh nghiệp đang chọn.
   *
   * Không dùng `getById()` ở đây vì helper đó có fallback cache tổng hợp để
   * phục vụ các màn hình quản trị. Conversation phải giữ đúng business hiện tại:
   * assignee cũ sau khi page đổi doanh nghiệp được hiển thị là `?`.
   */
  resolveCurrentBusinessUser(
    userId: string | number,
    users?: readonly IUserBusiness[] | null,
  ): ICurrentBusinessUserIdentity {
    const id = String(userId ?? '').trim();
    const businessId = this.auth.businessCurrent?.id;
    const candidates = users ?? (businessId ? this.getAll(String(businessId)) : []);
    const user = candidates.find(item => String(item.id) === id || String(item.userId) === id);

    if (!user) {
      return {id, name: '?', avatarUrl: '', found: false};
    }

    return {
      id,
      name: getUserDisplayName(user, '?'),
      avatarUrl: this.resolveAvatar(user),
      found: true,
    };
  }

  /**
   * Resolve URL avatar theo priority: avatar > fbUid graph > default.
   */
  getAvatar(userId: string | number, businessId?: string): string {
    const user = this.getById(userId, businessId);
    if (!user) return DEFAULT_AVATAR_URL;
    return this.resolveAvatar(user);
  }

  /**
   * Lấy tên hiển thị của nhân viên (từ cache).
   * Ưu tiên: userDisplayNamePos > userDisplayName > displayName > email > userId.
   */
  getDisplayName(userId: string | number, businessId?: string): string {
    const user = this.getById(userId, businessId);
    if (!user) return String(userId);
    return getUserDisplayName(user, String(user.userId ?? user.id));
  }

  /**
   * Đồng bộ toàn bộ user từ các business trong _store vào signal _allUsers (deduplicate theo ID).
   */
  private syncAllUsers(): void {
    const userMap = new Map<string, IUserBusiness>();
    for (const list of this._store().values()) {
      for (const u of list) {
        const uid = String(u.id ?? u.userId ?? '');
        if (uid && !userMap.has(uid)) {
          userMap.set(uid, u);
        }
      }
    }
    for (const u of this._allUsers()) {
      const uid = String(u.id ?? u.userId ?? '');
      if (uid && !userMap.has(uid)) {
        userMap.set(uid, u);
      }
    }
    this._allUsers.set(Array.from(userMap.values()));
  }

  // ── Mutations (optimistic update vào store) ───────────────────────────────
  // Các hàm này KHÔNG gọi API — caller chịu trách nhiệm gọi BE trước.
  // Sau khi BE trả OK, gọi hàm này để đồng bộ store ngay lập tức.

  /**
   * Thêm 1 nhân viên mới vào store (sau khi BE thêm thành công).
   */
  addUserToStore(user: IUserBusiness, businessId?: string): void {
    const bid = businessId ?? String(user.businessId ?? this.auth.businessCurrent?.id ?? '');
    if (!bid) return;

    const prev = this._store();
    const list = prev.get(bid) ?? [];
    const next = new Map(prev);
    next.set(bid, [...list, user]);

    this._store.set(next);
  }

  /**
   * Cập nhật thông tin 1 nhân viên trong store (sau khi BE sửa thành công).
   * Hợp nhất dữ liệu mới vào object cũ.
   */
  updateUserInStore(userId: string | number, data: Partial<IUserBusiness>, businessId?: string): void {
    const bid = businessId ?? String(this.auth.businessCurrent?.id ?? '');
    if (!bid) return;

    const prev = this._store();
    const list = prev.get(bid);
    if (!list) return;

    const strId = String(userId);
    const updated = list.map(u =>
      String(u.id) === strId || String(u.userId) === strId ? {...u, ...data} : u,
    );

    const next = new Map(prev);
    next.set(bid, updated);
    this._store.set(next);
  }

  /**
   * Xoá 1 nhân viên khỏi store (sau khi BE xoá thành công).
   */
  removeUserFromStore(userId: string | number, businessId?: string): void {
    const bid = businessId ?? String(this.auth.businessCurrent?.id ?? '');
    if (!bid) return;

    const prev = this._store();
    const list = prev.get(bid);
    if (!list) return;

    const strId = String(userId);
    const filtered = list.filter(u => String(u.id) !== strId && String(u.userId) !== strId);

    const next = new Map(prev);
    next.set(bid, filtered);
    this._store.set(next);
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  /**
   * Phân tích response từ API `/business/user/list`.
   * Xử lý nhiều format khác nhau (V1, V2, items/result/Array).
   */
  private extractItems(res: any): IUserBusiness[] {
    if (Array.isArray(res)) return res;
    if (res?.items) return res.items;
    if (res?.data?.items) return res.data.items;
    if (res?.data?.result) return res.data.result;
    if (Array.isArray(res?.data)) return res.data;
    return [];
  }

  /**
   * Kiểm tra nhân viên có phải "staff thuần" (không phải giám đốc/admin).
   */
  private isStaff(user: IUserBusiness): boolean {
    // Có roleId thì chỉ xét roleId như FE13 (displayUser): role số có thể vẫn là 100 dù đã đổi sang vai trò nhân viên
    if (user.roleId) return !EXCLUDED_ROLE_IDS_STRING.includes(String(user.roleId));
    // Bản ghi cũ chưa có roleId: dùng role số
    return !(user.role && EXCLUDED_ROLE_IDS_NUMERIC.includes(user.role));
  }

  /**
   * Resolve avatar ưu tiên: avatar > fbUid > default.
   */
  private resolveAvatar(user: IUserBusiness): string {
    if (user.avatar) return user.avatar;
    if (user.fbUid) return GRAPH_PICTURE(user.fbUid);
    return DEFAULT_AVATAR_URL;
  }
}
